import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const drResourceQueryKeys = {
  all: ['drResources'],
  lists: (params) => [...drResourceQueryKeys.all, 'list', params],
  list: (params) => [...drResourceQueryKeys.lists(params)],
  details: () => [...drResourceQueryKeys.all, 'detail'],
  detail: (id) => [...drResourceQueryKeys.details(), id],
  batch: (ids) => [...drResourceQueryKeys.all, 'batch', ids],
};

// Region Cluster Query Keys
export const regionClusterQueryKeys = {
  all: ['regionCluster'],
  sigunguEupmyeondong: (sigungu) => [...regionClusterQueryKeys.all, 'sigungu', sigungu, 'eupmyeondong'],
  sidoSigungu: (sido) => [...regionClusterQueryKeys.all, 'sido', sido, 'sigungu'],
  statsPack: (params) => [...regionClusterQueryKeys.all, 'statsPack', params],
};

// DR 자원 목록 조회
export const useDRResources = (params = {}) => {
  return useQuery({
    queryKey: drResourceQueryKeys.list(params),
    queryFn: () => api.get('/drivecore/resource/api/DrResource', { params }),
    staleTime: 5 * 60 * 1000,
    // 페이지별로 캐시를 구분하기 위한 설정
    keepPreviousData: false,
  });
};

// DR 자원 전체 목록 조회 (모든 페이지)
export const useDRResourcesAll = (params = {}, options = {}) => {
  // enabled 조건: options.enabled가 있으면 그것을 사용, 없으면 RegionCode가 있을 때만 실행
  const isEnabled = options.enabled !== undefined ? options.enabled : !!params.RegionCode;
  
  return useQuery({
    queryKey: [...drResourceQueryKeys.all, 'all', params],
    queryFn: async () => {
      // 추가 검증: RegionCode가 없으면 에러 throw (실제로는 enabled로 막히지만 방어적 코드)
      if (!params.RegionCode) {
        throw new Error('RegionCode is required for DR resources query');
      }
      
      const allItems = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await api.get('/drivecore/resource/api/DrResource', { 
          params: { ...params, Page: page, PageSize: 10 } 
        });
        
        if (response?.Items && response.Items.length > 0) {
          allItems.push(...response.Items);
          page++;
          
          // 마지막 페이지인지 확인
          if (response.Items.length < 10) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      
      return {
        Items: allItems,
        TotalCount: allItems.length,
        Page: 1,
        PageSize: allItems.length
      };
    },
    staleTime: 5 * 60 * 1000,
    ...options,
    // enabled는 마지막에 설정하여 위에서 계산한 값을 사용
    enabled: isEnabled,
  });
};

// DR 자원 상세 조회
export const useDRResource = (id, options = {}) => {
  // id가 null, undefined, 빈 문자열인 경우 enabled를 false로 설정
  const hasValidId = id !== null && id !== undefined && id !== '';
  
  // options.enabled가 명시적으로 전달되면 그것을 사용하되, id가 유효해야 함
  // options.enabled가 없으면 id가 유효할 때만 enabled
  const isEnabled = hasValidId && (options.enabled !== undefined ? options.enabled : true);
  
  // options에서 enabled를 제거하고 나중에 덮어쓰기
  const { enabled: _, ...restOptions } = options;
  
  return useQuery({
    queryKey: drResourceQueryKeys.detail(id),
    queryFn: async () => {
      // id가 없으면 에러 발생 (방어적 코드)
      if (!hasValidId) {
        throw new Error('DR Resource ID is required');
      }
      const response = await api.get(`/drivecore/resource/api/DrResource/${id}`);
      // 단일 DR 자원 조회의 경우 Items[0] 또는 직접 응답 데이터 반환
      return response?.Items?.[0] || response || null;
    },
    ...restOptions,
    enabled: isEnabled, // id가 유효하고 enabled 옵션이 true일 때만 요청
  });
};

// DR 자원 배치 조회
export const useDRResourcesBatch = (drResourceIds) => {
  return useQuery({
    queryKey: drResourceQueryKeys.batch(drResourceIds),
    queryFn: async () => {
      if (!drResourceIds || drResourceIds.length === 0) return [];
      const promises = drResourceIds.map(async (id) => {
        const response = await api.get(`/drivecore/resource/api/DrResource/${id}`);
        return response || null;
      });
      const results = await Promise.all(promises);
      // null 값 필터링
      return results.filter(drResource => drResource !== null);
    },
    enabled: !!drResourceIds && drResourceIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

// DR 자원 생성
export const useCreateDRResource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (drResourceData) => api.post('/drivecore/resource/api/DrResource', drResourceData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: drResourceQueryKeys.lists() });
    },
  });
};

// DR 자원 수정
export const useUpdateDRResource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ data }) => api.put(`/drivecore/resource/api/DrResource`, data),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(drResourceQueryKeys.detail(variables.data.drResourceSysId), data);
      queryClient.invalidateQueries({ queryKey: drResourceQueryKeys.lists() });
    },
  });
};

// DR 자원 삭제
export const useDeleteDRResource = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => api.delete(`/drivecore/resource/api/DrResource/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: drResourceQueryKeys.lists() });
    },
  });
};

// 시군구 선택 시 읍면동 마커들의 통계 정보 조회
export const useSigunguEupmyeondongStats = (sigungu, options = {}) => {
  return useQuery({
    queryKey: regionClusterQueryKeys.sigunguEupmyeondong(sigungu),
    queryFn: () => api.get(`/drivecore/resource/api/RegionCluster/${sigungu}/eupmyuondong`),
    staleTime: 5 * 60 * 1000,
    ...options,
    // enabled는 마지막에 설정하여 options.enabled가 있으면 그것을 사용
    enabled: options.enabled !== undefined ? options.enabled : true,
  });
};

// 시도 선택 시 시군구 마커들의 통계 정보 조회
export const useSidoSigunguStats = (sido) => {
  return useQuery({
    queryKey: regionClusterQueryKeys.sidoSigungu(sido),
    queryFn: () => api.get(`/drivecore/resource/api/RegionCluster/${sido}/sigungu`),
    enabled: true, // 항상 호출
    staleTime: 5 * 60 * 1000,
  });
};

// 주어진 충전소들의 통계 데이터 팩 (통합, 평균) 조회
export const useRegionClusterStatPack = (params, options = {}) => {
  const baseEnabled = params.StationSysIdList && params.StationSysIdList.length > 0;
  const isEnabled = baseEnabled && (options.enabled !== undefined ? options.enabled : true);
  
  return useQuery({
    queryKey: regionClusterQueryKeys.statsPack(params),
    queryFn: () => api.post('/drivecore/stat/api/RegionClusterStat/pack', params ),
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled: isEnabled,
  });
};